import React from 'react';
import axios from 'axios';
import type { FormikHelpers, FormikProps } from 'formik';
import { Formik, useFormikContext } from 'formik';
import { snakeCase, uniqBy } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import CheckboxFormik from '~/components/CheckboxFormik';
import WithConfirm from '~/components/ConfirmModal';
import ArrayMultiselectFieldWithChipsFormik from '~/components/core/ArrayMultiselectFieldWithChipsFormik';
import MenuItem from '~/components/core/Atomic/MenuItem';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import { Text } from '~/components/core/TextComponents';
import {
  useFormikValidationErrorToast,
  ValidationErrorToastContainer,
} from '~/components/hooks/useFormikValidationErrorToast';
import { CLIVE_EXCLUDED_FIELD_KEY } from '~/components/SystemConfiguration/CliveConfiguration/CliveConfigurationContext';
import { configuredFieldSelectOptionSchema } from '~/components/SystemConfiguration/FnolConfiguration/fnolConfigurationValidationSchema';
import TextFieldFormik from '~/components/TextFieldFormik';
import useDataFetcher from '~/components/useDataFetcher';
import { reportAxiosError } from '~/Utils';

import { toScopeFormValues, toScopePayload } from './contextScope';
import {
  getTypeSelectOptions,
  isTypeLocked,
  toDisplayedType,
  toStoredType,
  TYPES_WITH_OPTIONS,
} from './fieldTypeOptions';
import GlobalFieldContextPicker from './GlobalFieldContextPicker';
import GlobalFieldOptionsEditor from './GlobalFieldOptionsEditor';
import type { GlobalCategoryRow, GlobalFieldAnchor, GlobalFieldPlacement, GlobalFieldRow } from './types';
import {
  ANCHOR_LABELS,
  GLOBAL_FIELD_ANCHOR,
  GLOBAL_FIELD_PLACEMENT,
  globalCategoriesUrl,
  globalFieldsUrl,
  PLACEMENT_LABELS,
} from './types';

interface FieldOption {
  /** `id`, not `key`: the backend validates a stored option as a `ConfiguredFieldSelectOption`,
   *  whose identifier is `id`, and that is what a stored claim value holds. */
  id: string;
  desc: string;
  active: boolean;
  /** Client-only, stripped on submit: set on a row added in this dialog, whose key may still be
   *  derived from its name. A saved option's key is what stored claim values hold. */
  isNew?: boolean;
}

interface FormValues {
  key: string;
  desc: string;
  type: string;
  placement: GlobalFieldPlacement;
  anchor: GlobalFieldAnchor;
  mandatory: boolean;
  lobs: (string | number)[];
  sub_organization_ids: (string | number)[];
  options: FieldOption[];
  incident_sub_type_categories: string[];
}

// The key is the projected entry's `id` in every configuration blob, and a global category's limit
// list matches on it. Create-time only, as the categories sibling does: the key is immutable and
// never sent on PATCH, so checking it in edit mode would block an edit on a disabled field.
const buildValidationSchema = (isEdit: boolean) => {
  const key = Yup.string().required('Required').max(128);
  return Yup.object().shape({
    desc: Yup.string().required('Required'),
    type: Yup.string().required('Required'),
    key: isEdit ? key : key.matches(/^[a-z0-9_]+$/, 'Key must use lowercase letters, digits and underscores only'),
    // The BE refuses a narrowed scope that names nothing; `'all'` counts as an entry here.
    lobs: Yup.array().min(1, 'Select at least one line of business'),
    sub_organization_ids: Yup.array().min(1, 'Select at least one sub-organization'),
    // The FNOL screen's option rules: `ConfiguredField` refuses an empty list or a blank option,
    // and two names that derive the same key would store one key for two labels.
    options: Yup.array().when('type', {
      is: (type: string) => TYPES_WITH_OPTIONS.includes(type),
      then: (schema) =>
        schema
          .of(configuredFieldSelectOptionSchema)
          .min(1, 'At least one option is required')
          .test(
            'unique-ids',
            'Option keys must be unique',
            (value) => uniqBy(value, 'id').length === (value ?? []).length
          ),
    }),
  });
};

interface GlobalFieldDialogProps {
  organizationId: number;
  /** The placements this tab owns. One entry means the select is fixed and hidden. */
  placements: GlobalFieldPlacement[];
  /** null = create; a row = edit (key immutable, not sent on PATCH). */
  field: GlobalFieldRow | null;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}

const GlobalFieldDialog: React.FC<GlobalFieldDialogProps> = ({
  organizationId,
  placements,
  field,
  onClose,
  onSaved,
}) => {
  const definition = (field?.definition ?? {}) as Record<string, unknown>;

  const initialValues: FormValues = {
    key: field?.key ?? '',
    desc: String(definition.desc ?? ''),
    type: toDisplayedType(definition),
    placement: field?.placement ?? placements[0],
    anchor: field?.anchor ?? GLOBAL_FIELD_ANCHOR.End,
    mandatory: definition.mandatory === true,
    ...toScopeFormValues(field),
    // A stored option without `active` is active: the backend's option schema defaults it to true.
    options: ((definition.options as FieldOption[] | undefined) ?? []).map((option) => ({
      ...option,
      active: option.active !== false,
    })),
    incident_sub_type_categories: (definition.incident_sub_type_categories as string[]) ?? [],
  };

  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    const baseUrl = globalFieldsUrl(organizationId);
    // Starts from the stored definition because the BE replaces it wholesale: a key this dialog
    // does not author (e.g. `claim_closing_mandatory`, set through the API) must survive an edit
    // made here. The
    // projection-owned keys (`id`, `active`, `section`) never reach a stored definition, as the BE
    // refuses them on every write.
    const {
      options: _storedOptions,
      incident_sub_type_categories: _storedCategories,
      disable_na: _storedDisableNa,
      disable_unknown: _storedDisableUnknown,
      ...preservedDefinition
    } = definition;
    const fieldDefinition: Record<string, unknown> = {
      ...preservedDefinition,
      desc: values.desc,
      ...toStoredType(values.type),
      mandatory: values.mandatory,
      // `ConfiguredField` refuses a mandatory field that is excluded from Clive, and a stored
      // definition can carry the exclusion (set through the API; this dialog never authors it and
      // `preservedDefinition` keeps it). Clearing it mirrors what the per-configuration card does
      // on the same toggle, so turning Required on cannot 400 on a key the user never saw. Only
      // when the exclusion is actually set, so an ordinary edit does not write the key.
      ...(values.mandatory && preservedDefinition[CLIVE_EXCLUDED_FIELD_KEY]
        ? { [CLIVE_EXCLUDED_FIELD_KEY]: false }
        : {}),
      ...(TYPES_WITH_OPTIONS.includes(values.type)
        ? { options: values.options.map(({ isNew: _isNew, ...option }) => option) }
        : {}),
      ...(values.incident_sub_type_categories.length
        ? { incident_sub_type_categories: values.incident_sub_type_categories }
        : {}),
    };
    const scope = toScopePayload(values);

    try {
      if (field) {
        // `key` is immutable and the PATCH schema rejects unexpected fields (extra='forbid').
        await axios.patch(`${baseUrl}/${field.id}`, {
          definition: fieldDefinition,
          placement: values.placement,
          anchor: values.anchor,
          ...scope,
        });
      } else {
        await axios.post(baseUrl, {
          key: values.key,
          definition: fieldDefinition,
          placement: values.placement,
          anchor: values.anchor,
          ...scope,
        });
      }
      await onSaved();
      onClose();
    } catch (error) {
      setSubmitting(false);
      await reportAxiosError(error);
    }
  };

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={buildValidationSchema(!!field)}
    >
      <GlobalFieldDialogInner
        organizationId={organizationId}
        placements={placements}
        isEdit={!!field}
        savedType={initialValues.type}
        onClose={onClose}
      />
    </Formik>
  );
};

interface GlobalFieldDialogInnerProps {
  organizationId: number;
  placements: GlobalFieldPlacement[];
  isEdit: boolean;
  /** The type the field was opened with, which decides what the Type select may change to. */
  savedType: string;
  onClose: () => void;
}

const GlobalFieldDialogInner: React.FC<GlobalFieldDialogInnerProps> = ({
  organizationId,
  placements,
  isEdit,
  savedType,
  onClose,
}) => {
  const { handleSubmit, isSubmitting, setFieldValue, values }: FormikProps<FormValues> = useFormikContext<FormValues>();
  const [isKeyEdited, setIsKeyEdited] = React.useState(false);

  // Only global categories are offered. A per-configuration category key would resolve on some
  // forms and not others, which is the one thing a field defined once for the organization must
  // not do — and the BE refuses one, so offering it would only produce a save error.
  const { data: categories } = useDataFetcher<GlobalCategoryRow[]>(globalCategoriesUrl(organizationId));
  const categoryOptions = (categories ?? []).map((category) => ({
    id: category.key,
    value: category.key,
    label: category.is_enabled ? category.display_name : `${category.display_name} (disabled)`,
  }));

  const showPlacementSelect = placements.length > 1;
  // Categories limit which claims show a field, which is an FNOL idea: a policy field is rendered
  // from the policy, where no incident sub-type has been chosen.
  const showCategoryLimit = values.placement !== GLOBAL_FIELD_PLACEMENT.Policy;

  useFormikValidationErrorToast({
    toastId: 'global-field-errors',
    title: 'Please fix the following before saving:',
  });

  return (
    <CardDialog
      title={isEdit ? `Edit Global Field - ${values.desc}` : 'Add Global Field'}
      isDialog
      open
      maxWidth="md"
      fullWidth
      onClose={onClose}
      preventClose={isSubmitting}
      footerActions={
        <DialogFooterActions
          onClickPrimary={handleSubmit}
          onClickSecondary={onClose}
          primaryLabel="Save"
          showPrimary
          secondaryLabel="Cancel"
          disabled={isSubmitting}
        />
      }
    >
      <ValidationErrorToastContainer containerId="global-field-errors" />
      <div className="flex flex-col gap-24">
        <div className="grid grid-cols-2 gap-24">
          <TextFieldFormik
            id="desc"
            label="Display Name"
            fullWidth
            // @ts-expect-error TextFieldFormik is an untyped JSX module, so its `onChange` prop is
            // inferred as the `undefined` default it destructures. Fix the types when it is converted.
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue('desc', event.target.value);
              // The key mirrors the name until the field exists; afterwards it is immutable,
              // because every projected entry and every stored value is keyed by it.
              if (!isEdit && !isKeyEdited) {
                setFieldValue('key', snakeCase(event.target.value));
              }
            }}
          />
          <TextFieldFormik
            id="key"
            label="Key"
            fullWidth
            disabled={isEdit}
            // @ts-expect-error see the `onChange` note above.
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setIsKeyEdited(true);
              setFieldValue('key', event.target.value);
            }}
          />
          <SingleSelectField
            id="type"
            label="Type"
            value={values.type}
            onChange={(value: string) => setFieldValue('type', value)}
            disabled={isTypeLocked(isEdit, savedType)}
            fullWidth
          >
            {getTypeSelectOptions(isEdit, savedType).map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.desc}
              </MenuItem>
            ))}
          </SingleSelectField>
          {showPlacementSelect ? (
            <SingleSelectField
              id="placement"
              label="Placement"
              value={values.placement}
              onChange={(value: GlobalFieldPlacement) => setFieldValue('placement', value)}
              fullWidth
            >
              {placements.map((placement) => (
                <MenuItem key={placement} value={placement}>
                  {PLACEMENT_LABELS[placement]}
                </MenuItem>
              ))}
            </SingleSelectField>
          ) : null}
          <SingleSelectField
            id="anchor"
            label="Position"
            value={values.anchor}
            onChange={(value: GlobalFieldAnchor) => setFieldValue('anchor', value)}
            fullWidth
          >
            {Object.entries(ANCHOR_LABELS).map(([anchor, label]) => (
              <MenuItem key={anchor} value={anchor}>
                {label}
              </MenuItem>
            ))}
          </SingleSelectField>
        </div>

        {/* Same control and label the per-configuration `FieldConfigurationCard` carries, behind the
            same turn-on-only confirmation. The copy widens that card's warning: a global field is
            projected into every configuration in its context, so one tick makes the field required
            in every one of their published FNOL schemas at once. */}
        <WithConfirm
          title="Change Field to be Required?"
          contentText={
            'This will be a breaking change for the POST: FNOL API on every configuration in this ' +
            "field's context. Are you sure you want to proceed?"
          }
          primaryButtonName="YES, PROCEED"
          shouldCloseOnPrimary
          triggerMethod="onChange"
          centerDialog
          disableConfirm={values.mandatory}
        >
          <CheckboxFormik
            id="mandatory"
            label="Required field for claim submission"
            // Explicit, not Formik's own handler: `WithConfirm` performs the change by calling
            // `children.props[triggerMethod]()`, so a checkbox without an `onChange` leaves the
            // modal calling `undefined` -- it never closes and the box never ticks.
            onChange={() => setFieldValue('mandatory', !values.mandatory)}
          />
        </WithConfirm>

        {TYPES_WITH_OPTIONS.includes(values.type) ? <GlobalFieldOptionsEditor /> : null}

        <GlobalFieldContextPicker />

        {showCategoryLimit ? (
          <div className="flex flex-col gap-8">
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
              Limit to Categories
            </Text>
            <ArrayMultiselectFieldWithChipsFormik
              id="incident_sub_type_categories"
              label="Global Categories"
              options={categoryOptions}
              disabled={categoryOptions.length === 0}
            />
            <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
              Leave empty to show this field on every claim. Naming a category shows it only when that category matches
              the claim&apos;s policy.
            </Text>
          </div>
        ) : null}
      </div>
    </CardDialog>
  );
};

export default GlobalFieldDialog;
